import { api, refreshSession, redirectToLogin } from './api';
import type {
  Ticket,
  Customer,
  Team,
  CannedReply,
  Notification,
  Message,
  Attachment,
  TicketStatus,
  Prioritet,
  Category,
  RoutingRule,
} from './types';

// ---------- Tickets ----------
export interface TicketListResponse {
  tickets: (Ticket & {
    customer?: { id: string; navn: string | null; epost: string } | null;
    assignee?: { id: string; navn: string; avatarUrl?: string | null } | null;
    team?: { id: string; navn: string } | null;
    _count?: { messages: number };
    // Ulest-sporing + siste aktivitet (beregnes server-side per innlogget bruker)
    ulest: boolean;
    sistAktivitet: string;
    lastMessage: {
      id: string;
      opprettet: string;
      type: string;
      authorId?: string | null;
      innholdTekst?: string | null;
    } | null;
  })[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export const ticketsApi = {
  list: (params?: {
    filter?: string;
    status?: TicketStatus;
    teamId?: string;
    q?: string;
    inboxScope?: 'active' | 'closed';
    page?: number;
  }) => {
    const query = new URLSearchParams();
    if (params?.filter) query.set('filter', params.filter);
    if (params?.status) query.set('status', params.status);
    if (params?.teamId) query.set('teamId', params.teamId);
    if (params?.q) query.set('q', params.q);
    if (params?.inboxScope) query.set('inboxScope', params.inboxScope);
    if (params?.page) query.set('page', String(params.page));
    return api.get<TicketListResponse>(`/tickets?${query.toString()}`);
  },

  get: (id: string) => api.get<Ticket>(`/tickets/${id}`),

  create: (data: {
    tittel: string;
    beskrivelse?: string;
    prioritet?: Prioritet;
    kategori?: string;
    customerId?: string;
    customerEmail?: string;
    customerName?: string;
    assigneeId?: string;
    teamId?: string;
  }) => api.post<Ticket>('/tickets', data),

  update: (id: string, data: Partial<Pick<Ticket, 'tittel' | 'beskrivelse' | 'status' | 'prioritet' | 'kategori'>>) =>
    api.patch<Ticket>(`/tickets/${id}`, data),

  assign: (id: string, assigneeId: string | null, teamId?: string | null) =>
    api.patch<Ticket>(`/tickets/${id}/assign`, { assigneeId, teamId }),

  selfAssign: (id: string) => api.post<Ticket>(`/tickets/${id}/self-assign`),

  markRead: (id: string, lestTil: string) =>
    api.post<{ success: boolean; lestTil: string }>(`/tickets/${id}/read`, { lestTil }),

  remove: (id: string) => api.delete<{ success: boolean }>(`/tickets/${id}`),

  // ---------- Messages ----------
  messages: (ticketId: string) => api.get<Message[]>(`/tickets/${ticketId}/messages`),

  createMessage: (
    ticketId: string,
    data: { type: 'KUNDE_SVAR' | 'INTERNT_NOTAT'; innholdHtml: string; innholdTekst?: string; attachmentIds?: string[] },
  ) => api.post<Message>(`/tickets/${ticketId}/messages`, data),
};

// ---------- Attachments ----------
export const attachmentsApi = {
  list: (ticketId: string) => api.get<Attachment[]>(`/tickets/${ticketId}/attachments`),

  upload: (ticketId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return fetch(`/api/tickets/${ticketId}/attachments/upload`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    }).then(async (res) => {
      if (res.status === 401) {
        // Prøv refresh (delt/deduplisert via api.ts), deretter retry én gang
        const refreshed = await refreshSession();
        if (refreshed) {
          const retry = await fetch(`/api/tickets/${ticketId}/attachments/upload`, {
            method: 'POST',
            credentials: 'include',
            body: formData,
          });
          if (retry.ok) return retry.json();
          if (retry.status === 401) {
            redirectToLogin();
            throw new Error('Ikke innlogget');
          }
          const err = await retry.json().catch(() => ({ message: retry.statusText }));
          throw new Error(err.message || `Upload feilet (${retry.status})`);
        }
        redirectToLogin();
        throw new Error('Ikke innlogget');
      }
      if (!res.ok) {
        const err = await res.json().catch(() => ({ message: res.statusText }));
        throw new Error(err.message || `Upload feilet (${res.status})`);
      }
      return res.json();
    });
  },

  downloadUrl: (ticketId: string, attachmentId: string) =>
    `/api/tickets/${ticketId}/attachments/${attachmentId}/download`,

  // Hent vedlegg via fetch med 401→refresh→retry, returner blob URL
  download: async (ticketId: string, attachmentId: string): Promise<string> => {
    const url = `/api/tickets/${ticketId}/attachments/${attachmentId}/download`;
    const doFetch = () => fetch(url, { credentials: 'include', redirect: 'follow' });

    let res = await doFetch();
    if (res.status === 401) {
      const refreshed = await refreshSession();
      if (refreshed) {
        res = await doFetch();
      }
      if (res.status === 401) {
        redirectToLogin();
        throw new Error('Ikke innlogget');
      }
    }
    if (!res.ok) throw new Error(`Nedlasting feilet (${res.status})`);

    // Backend redirecter til presigned MinIO URL – følg redirect og hent blob
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  },

  remove: (ticketId: string, attachmentId: string) =>
    api.delete(`/tickets/${ticketId}/attachments/${attachmentId}`),
};

// ---------- Teams ----------
export const teamsApi = {
  list: () => api.get<Team[]>('/teams'),
};

// ---------- Customers ----------
export interface CustomerWithStats extends Customer {
  _count: { tickets: number };
  byStatus: Record<string, number>;
}

export interface CustomerDetail extends Customer {
  _count: { tickets: number };
  byStatus: Record<string, number>;
  tickets: Array<{
    id: string;
    sakNr: number;
    tittel: string;
    status: string;
    prioritet: string;
    kategori: string | null;
    opprettet: string;
    assignee: { id: string; navn: string } | null;
  }>;
}

export const customersApi = {
  list: (q?: string) =>
    api.get<CustomerWithStats[]>(`/customers${q ? `?q=${encodeURIComponent(q)}` : ''}`),
  get: (id: string) => api.get<CustomerDetail>(`/customers/${id}`),
  create: (data: { epost: string; navn?: string; telefon?: string; firma?: string }) =>
    api.post<Customer>('/customers', data),
  update: (id: string, data: { navn?: string; telefon?: string; firma?: string }) =>
    api.patch<Customer>(`/customers/${id}`, data),
};

// ---------- Canned replies ----------
export const cannedRepliesApi = {
  list: () => api.get<CannedReply[]>('/canned-replies'),
  getBySnarvei: (snarvei: string) => api.get<CannedReply>(`/canned-replies/${snarvei}`),
};

// ---------- Auth / egne passkeys ----------
export const authApi = {
  addKeyOptions: () => api.post<{ options: any; userId: string }>('/auth/add-key/options'),
  verifyAddKey: (data: { credential: any; deviceNavn?: string }) =>
    api.post<{ success: true }>('/auth/add-key/verify', data),
  listMyKeys: () => api.get<Credential[]>('/auth/keys'),
  deleteMyKey: (credId: string) => api.delete(`/auth/keys/${credId}`),
};

// ---------- Notifications ----------
export const notificationsApi = {
  list: () => api.get<Notification[]>('/notifications'),
  unreadCount: () => api.get<{ count: number }>('/notifications/unread/count'),
  markRead: (id: string) => api.post(`/notifications/${id}/read`),
  markAllRead: () => api.post('/notifications/read-all'),
};

// ---------- Users ----------
export interface UserWithCreds {
  id: string;
  navn: string;
  epost: string;
  rolle: 'ADMIN' | 'AGENT' | 'VIEWER';
  aktiv: boolean;
  avatarUrl?: string | null;
  opprettet: string;
}

export interface Credential {
  id: string;
  deviceNavn: string | null;
  transports: string[];
  opprettet: string;
  sistBrukt: string | null;
}

export const usersApi = {
  list: () => api.get<UserWithCreds[]>('/users'),
  get: (id: string) => api.get<UserWithCreds>(`/users/${id}`),
  create: (data: { navn: string; epost: string; rolle?: string }) =>
    api.post<UserWithCreds>('/users', data),
  update: (id: string, data: { navn?: string; rolle?: string; aktiv?: boolean }) =>
    api.patch<UserWithCreds>(`/users/${id}`, data),
  listCredentials: (id: string) => api.get<Credential[]>(`/users/${id}/credentials`),
  removeCredential: (userId: string, credId: string) =>
    api.delete(`/users/${userId}/credentials/${credId}`),

  // Kategori-tilgang
  getCategories: (id: string) =>
    api.get<Array<{ id: string; navn: string; farge: string }>>(`/users/${id}/categories`),
  setCategories: (id: string, categoryIds: string[]) =>
    api.put<Array<{ id: string; navn: string; farge: string }>>(`/users/${id}/categories`, { categoryIds }),
};

// ---------- Categories + Routing ----------
export const categoriesApi = {
  list: () => api.get<Category[]>('/categories'),
  listActive: () => api.get<Category[]>('/categories/active'),
  create: (data: { navn: string; beskrivelse?: string; farge?: string }) =>
    api.post<Category>('/categories', data),
  update: (id: string, data: { navn?: string; beskrivelse?: string; farge?: string; aktiv?: boolean }) =>
    api.patch<Category>(`/categories/${id}`, data),
  remove: (id: string) => api.delete(`/categories/${id}`),

  // Routing rules
  createRule: (data: {
    categoryId: string;
    assigneeId?: string;
    teamId?: string;
    prioritet?: Prioritet;
    nokkelord?: string;
  }) => api.post<RoutingRule>('/categories/routing-rules', data),

  updateRule: (id: string, data: {
    assigneeId?: string | null;
    teamId?: string | null;
    prioritet?: Prioritet | null;
    nokkelord?: string | null;
    aktiv?: boolean;
  }) => api.patch<RoutingRule>(`/categories/routing-rules/${id}`, data),

  removeRule: (id: string) => api.delete(`/categories/routing-rules/${id}`),
};
